import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  extractHtmlFragment,
  normalizeVisibility,
  sanitizeEmbeddedHtml,
  sanitizeHtml,
} from './editorUtils';

const VISIBILITY_BADGES = {
  mobile: { label: 'Nur Mobil', Icon: PhoneIphoneIcon },
  desktop: { label: 'Nur Desktop', Icon: DesktopWindowsIcon },
};
import { BRAND_SECONDARY_MAIN } from '../../theme/managementTheme';

const alignToMargin = align => {
  if (align === 'left') return '0 auto 0 0';
  if (align === 'right') return '0 0 0 auto';
  return '0 auto';
};

// Approximate visual rendering of a single element. Visual fidelity comes from
// the server-rendered preview; this only needs to be close enough for editing.
const ElementVisual = ({ element }) => {
  switch (element.type) {
    case 'text':
      return (
        <Box
          sx={{
            p: `${element.padding ?? 12}px`,
            fontSize: element.fontSize ? `${element.fontSize}px` : 'inherit',
            color: element.color || 'inherit',
            textAlign: element.align || 'left',
            wordBreak: 'break-word',
            '& p': { m: 0 },
            '& a': { color: 'inherit' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(element.html) }}
        />
      );
    case 'image':
      return (
        <Box sx={{ p: `${element.padding ?? 12}px` }}>
          {element.src ? (
            <Box
              component='img'
              src={element.src}
              alt={element.alt || ''}
              sx={{
                display: 'block',
                width: `${element.widthPercent ?? 100}%`,
                maxWidth: '100%',
                margin: alignToMargin(element.align || 'center'),
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 4,
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 1,
                color: 'text.secondary',
                backgroundColor: 'grey.50',
              }}
            >
              <ImageOutlinedIcon />
              <Typography variant='caption'>Kein Bild ausgewählt</Typography>
            </Box>
          )}
        </Box>
      );
    case 'button':
      return (
        <Box sx={{ p: `${element.padding ?? 12}px`, textAlign: element.align || 'center' }}>
          <Box
            component='span'
            sx={{
              display: 'inline-block',
              backgroundColor: element.backgroundColor || BRAND_SECONDARY_MAIN,
              color: element.textColor || '#ffffff',
              borderRadius: `${element.borderRadius ?? 6}px`,
              px: `${(element.innerPadding ?? 12) * 2}px`,
              py: `${element.innerPadding ?? 12}px`,
              fontSize: element.fontSize ? `${element.fontSize}px` : 'inherit',
              fontWeight: 600,
              cursor: 'default',
            }}
          >
            {element.label || 'Button'}
          </Box>
        </Box>
      );
    case 'spacer':
      return <Box sx={{ height: `${element.height ?? 20}px`, minHeight: 4 }} />;
    case 'divider':
      return (
        <Box
          sx={{
            pt: `${element.paddingTop ?? 12}px`,
            pb: `${element.paddingBottom ?? 12}px`,
          }}
        >
          <Box
            sx={{
              width: `${element.widthPercent ?? 100}%`,
              mx: 'auto',
              borderTop: `${element.thickness ?? 1}px solid ${element.color || '#dddddd'}`,
            }}
          />
        </Box>
      );
    case 'html':
      return String(element.html || '').trim() ? (
        <Box
          sx={{
            p: `${element.padding ?? 0}px`,
            wordBreak: 'break-word',
            '& img': { maxWidth: '100%', height: 'auto' },
            '& table': { maxWidth: '100%' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeEmbeddedHtml(extractHtmlFragment(element.html)) }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 4,
            m: `${element.padding ?? 0}px`,
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: 1,
            color: 'text.secondary',
            backgroundColor: 'grey.50',
          }}
        >
          <CodeOutlinedIcon />
          <Typography variant='caption'>Noch kein HTML hinterlegt</Typography>
        </Box>
      );
    default:
      return null;
  }
};

// One sortable element on the canvas with selection border and hover actions
// (drag handle, duplicate, save to "Meine Elemente", delete).
const CanvasElement = ({
  element,
  sectionId,
  index,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onSaveElement,
  canEdit,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.elementId,
    data: { from: 'canvas', sectionId, index },
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };
  const badge = VISIBILITY_BADGES[normalizeVisibility(element.visibility)];

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={event => {
        event.stopPropagation();
        onSelect({ sectionId, elementId: element.elementId });
      }}
      sx={{
        position: 'relative',
        border: '2px solid',
        borderColor: isSelected ? BRAND_SECONDARY_MAIN : 'transparent',
        cursor: 'pointer',
        '&:hover': { borderColor: isSelected ? BRAND_SECONDARY_MAIN : 'grey.400' },
        '&:hover .fz-element-actions, &:focus-within .fz-element-actions': { opacity: 1 },
        ...(isSelected ? { '& .fz-element-actions': { opacity: 1 } } : {}),
      }}
    >
      <ElementVisual element={element} />
      {badge && (
        <Chip
          size='small'
          icon={<badge.Icon />}
          label={badge.label}
          sx={{
            position: 'absolute',
            top: 2,
            left: 2,
            height: 20,
            fontSize: 10,
            zIndex: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiChip-icon': { fontSize: 12, ml: '4px' },
          }}
        />
      )}
      {canEdit && (
        <Box
          className='fz-element-actions'
          onClick={event => event.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            display: 'flex',
            gap: 0.25,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 2,
            opacity: 0,
            transition: 'opacity 120ms',
            zIndex: 3,
          }}
        >
          <Tooltip title='Verschieben'>
            <IconButton size='small' sx={{ cursor: 'grab', touchAction: 'none' }} {...attributes} {...listeners}>
              <DragIndicatorIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Duplizieren'>
            <IconButton size='small' onClick={() => onDuplicate(sectionId, element.elementId)}>
              <ContentCopyIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
          {onSaveElement && (
            <Tooltip title='Zu Meine Elemente speichern'>
              <IconButton size='small' onClick={() => onSaveElement(element)}>
                <BookmarkAddOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Löschen'>
            <IconButton size='small' color='error' onClick={() => onDelete(sectionId, element.elementId)}>
              <DeleteOutlineIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

// A section: vertical container of elements, droppable for palette / saved
// element drags and sortable context for reordering. Empty sections can be
// removed.
const CanvasSection = ({
  section,
  selected,
  onSelect,
  onDeleteSection,
  onDeleteElement,
  onDuplicateElement,
  onSaveElement,
  canEdit,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `section:${section.sectionId}`,
    data: { from: 'section', sectionId: section.sectionId },
    disabled: !canEdit,
  });

  const elementIds = section.elements.map(element => element.elementId);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        border: '1px dashed',
        borderColor: isOver ? BRAND_SECONDARY_MAIN : 'grey.300',
        m: 1,
        minHeight: 40,
        '&:hover .fz-section-actions': { opacity: 1 },
      }}
    >
      <SortableContext items={elementIds} strategy={verticalListSortingStrategy}>
        {section.elements.map((element, index) => (
          <CanvasElement
            key={element.elementId}
            element={element}
            sectionId={section.sectionId}
            index={index}
            isSelected={selected?.elementId === element.elementId}
            onSelect={onSelect}
            onDelete={onDeleteElement}
            onDuplicate={onDuplicateElement}
            onSaveElement={onSaveElement}
            canEdit={canEdit}
          />
        ))}
      </SortableContext>
      {section.elements.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 64,
            color: 'text.secondary',
          }}
        >
          <Typography variant='caption'>Elemente hierher ziehen</Typography>
        </Box>
      )}
      {canEdit && section.elements.length === 0 && (
        <Box
          className='fz-section-actions'
          sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, transition: 'opacity 120ms', zIndex: 3 }}
        >
          <Tooltip title='Leere Sektion entfernen'>
            <IconButton
              size='small'
              color='error'
              onClick={event => {
                event.stopPropagation();
                onDeleteSection(section.sectionId);
              }}
            >
              <DeleteOutlineIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

// The centered mail canvas: message style frame, sections with elements, the
// "Sektion hinzufügen" button and — for marketing mails — the mandatory footer
// block at the bottom.
const EditorCanvas = ({
  doc,
  selected,
  onSelect,
  onAddSection,
  onDeleteSection,
  onDeleteElement,
  onDuplicateElement,
  onSaveElement,
  canEdit,
  showFooterBlock = true,
}) => {
  const { messageStyle, sections, footer } = doc;
  const footerSelected = Boolean(selected?.footer);

  return (
    <Box
      onClick={() => onSelect(null)}
      sx={{ flex: 1, overflow: 'auto', backgroundColor: '#ececec', py: 4, px: 2 }}
    >
      <Box
        sx={{
          maxWidth: `${(messageStyle.width || 600) + 64}px`,
          mx: 'auto',
          backgroundColor: messageStyle.backgroundColor || '#f4f4f4',
          p: 3,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: `${messageStyle.width || 600}px`,
            mx: 'auto',
            backgroundColor: messageStyle.contentBackgroundColor || '#ffffff',
            fontFamily: messageStyle.fontFamily || 'Arial, Helvetica, sans-serif',
            fontSize: `${messageStyle.fontSize || 16}px`,
            color: messageStyle.textColor || '#333333',
          }}
        >
          {sections.map(section => (
            <CanvasSection
              key={section.sectionId}
              section={section}
              selected={selected}
              onSelect={onSelect}
              onDeleteSection={onDeleteSection}
              onDeleteElement={onDeleteElement}
              onDuplicateElement={onDuplicateElement}
              onSaveElement={onSaveElement}
              canEdit={canEdit}
            />
          ))}
          {canEdit && (
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                size='small'
                startIcon={<AddIcon />}
                onClick={event => {
                  event.stopPropagation();
                  onAddSection();
                }}
                sx={{ border: '1px dashed', borderColor: 'grey.400', color: 'text.secondary' }}
              >
                Sektion hinzufügen
              </Button>
            </Box>
          )}
          {showFooterBlock && (
            <Box
              onClick={event => {
                event.stopPropagation();
                onSelect({ footer: true });
              }}
              sx={{
                borderTop: '1px solid',
                borderTopColor: 'grey.300',
                outline: footerSelected ? `2px solid ${BRAND_SECONDARY_MAIN}` : 'none',
                outlineOffset: '-2px',
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Chip label='Pflichtbereich' size='small' sx={{ mb: 1 }} />
              <Typography variant='body2' sx={{ whiteSpace: 'pre-line', fontSize: '0.8em', color: 'inherit' }}>
                {footer.text || 'Impressums- und Fußzeilentext'}
              </Typography>
              <Typography
                variant='body2'
                component='span'
                sx={{ display: 'inline-block', mt: 1, textDecoration: 'underline', fontSize: '0.8em' }}
              >
                {footer.unsubscribeLabel || 'Abmelden'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EditorCanvas;
